import { ContentSubmissionUnit, ContentRequirement, isVoided } from '@foodfluence/shared';

interface GroupedContentItem {
  key: string;
  unit: ContentSubmissionUnit;
}

/**
 * Gets all content units for a specific requirement, sorted by key.
 *
 * Voided units are dropped here rather than at each caller, so group status, slot counts and the
 * upload modal's "X of Y" all agree with the requirement's own decremented `count`. Units are keyed
 * by map key, not array position, so removing one shifts nothing.
 */
export const getGroupedContent = (
  contentMap: Record<string, ContentSubmissionUnit>,
  requirementId: string
): GroupedContentItem[] => {
  return Object.entries(contentMap)
    .filter(([_, unit]) => unit.requirementId === requirementId && !isVoided(unit))
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([key, unit]) => ({ key, unit }));
};

/**
 * Checks if all content items in a group are approved
 */
export const isGroupApproved = (contentUnits: GroupedContentItem[]): boolean => {
  return contentUnits.length > 0 && contentUnits.every(({ unit }) => unit.status === 'approved');
};

/**
 * Checks if any content in the group needs resubmission
 */
export const doesGroupNeedResubmission = (contentUnits: GroupedContentItem[]): boolean => {
  return contentUnits.some(({ unit }) => unit.contentRejected || unit.status !== 'approved');
};

/**
 * Gets the overall status for a group of content, plus which unit in the
 * group should represent it (the one the influencer needs to act on, if any).
 *
 * A group is only "approved" when every unit in it is approved — a group
 * with 3 approved photos and 1 rejected/unsubmitted photo must still surface
 * as actionable, not silently advance to the next phase.
 */
export const getGroupStatus = (contentUnits: GroupedContentItem[]): {
  status: 'approved' | 'pendingVerification' | 'pendingSubmission';
  needsResubmission: boolean;
  feedback?: string;
  representative?: GroupedContentItem;
} => {
  if (contentUnits.length === 0) {
    return { status: 'pendingSubmission', needsResubmission: false };
  }

  const allApproved = contentUnits.every(({ unit }) => unit.status === 'approved');
  if (allApproved) {
    return { status: 'approved', needsResubmission: false };
  }

  const rejected = contentUnits.filter(({ unit }) => unit.contentRejected);
  const unsubmitted = contentUnits.find(({ unit }) => unit.status === 'pendingSubmission');
  const representative = rejected[0] ?? unsubmitted;

  if (representative) {
    // `needsResubmission` must reflect an actual rejection, not merely an
    // unsubmitted unit — otherwise a brand-new (never-submitted) piece, or a
    // partially-approved group, gets flagged as rejected by callers and shown
    // as red "Needs edits" with resubmission copy on first view.
    return {
      status: 'pendingSubmission',
      needsResubmission: rejected.length > 0,
      // Only surface feedback on the list card when a single item was rejected;
      // carousels can have different feedback per photo — see MultiContentUploadModal.
      feedback: rejected.length === 1
        ? (rejected[0].unit.feedback ?? 'Please resubmit rejected items')
        : undefined,
      representative
    };
  }

  return {
    status: 'pendingVerification',
    needsResubmission: false,
    feedback: 'Waiting for content review'
  };
};

/**
 * Gets empty content slots for a requirement
 */
export const getEmptyContentSlots = (
  contentRequirement: ContentRequirement | undefined,
  contentId: string,
  existingCount: number
): { key: string; unit: undefined }[] => {
  const requiredCount = contentRequirement ? (contentRequirement.count || 1) : 1;
  const remainingCount = Math.max(0, requiredCount - existingCount);

  return Array.from({ length: remainingCount }, (_, index) => ({
    key: `${contentId}_new_${Date.now()}_${index}`,
    unit: undefined
  }));
};

/**
 * Gets all content slots for a requirement (existing + empty slots)
 */
export const getAllContentSlots = (
  contentMap: Record<string, ContentSubmissionUnit>,
  contentRequirement: ContentRequirement | undefined,
  contentId: string
): (GroupedContentItem | { key: string; unit: undefined })[] => {
  const existingContent = getGroupedContent(contentMap, contentId);
  const emptySlots = getEmptyContentSlots(contentRequirement, contentId, existingContent.length);
  return [...existingContent, ...emptySlots];
};

/**
 * Checks if all required content items are covered
 */
export const areAllSlotsCovered = (
  contentRequirement: ContentRequirement | undefined,
  contentMap: Record<string, ContentSubmissionUnit>,
  uploadedFiles: Record<string, File>,
  contentId: string
): boolean => {
  const requiredCount = contentRequirement ? (contentRequirement.count || 1) : 1;
  const existingContent = getGroupedContent(contentMap, contentId);
  
  // For new content, we need all files
  if (existingContent.length === 0) {
    return Object.keys(uploadedFiles).length >= requiredCount;
  }
  
  // For existing content, we just need the changed files
  return Object.keys(uploadedFiles).length > 0;
};
